import type { Editor } from "@tiptap/react";
import { useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { InputRow } from "../components/Common/__styles__/Modal";
import { CommonModal } from "../components/Common/CommonModal";
import Modal from "../components/Common/Modal";
import { Text } from "../components/Inputs/react-hook-form";

interface LinkModalForm {
  text: string;
  url: string;
}

export const useLinkModal = (editor: Editor | null) => {
  const [showLinkModal, hideLinkModal] = useModal(() => {
    if (!editor) return null;

    let selectedText = "";
    const { state } = editor;
    const { from, to } = state.selection;
    selectedText = state.doc.textBetween(from, to, " ");

    const selectedUrl =
      editor.isActive("link") ? editor.getAttributes("link").href : "";

    const {
      handleSubmit,
      register,
      formState: { errors },
    } = useForm<LinkModalForm>({
      defaultValues: {
        text: selectedText,
        url: selectedUrl,
      },
    });

    const onCancel = () => {
      editor.commands.focus();
      editor.commands.setTextSelection(to);
      hideLinkModal();
    };

    const onSubmit = handleSubmit(data => {
      if (data.text && data.url) {
        editor
          .chain()
          .focus()
          .insertContent({
            type: "text",
            text: data.text,
            marks: [
              {
                type: "link",
                attrs: {
                  href: data.url,
                },
              },
            ],
          })
          .unsetMark("link")
          .run();
        hideLinkModal();
      }
    });

    const modalContent = (
      <>
        <InputRow>
          <Text
            label="Text"
            error={errors.text?.message}
            {...register("text", { required: "Text is required" })}
            onKeyDown={async e => {
              if (e.key === "Enter") {
                e.preventDefault();
                await onSubmit();
              }
            }}
          />
        </InputRow>
        <InputRow>
          <Text
            label="Link"
            error={errors.url?.message}
            {...register("url", {
              required: "Link is required",
              validate: val => {
                if (!val.startsWith("http://") && !val.startsWith("https://")) {
                  return "Link must start with http:// or https://";
                }
                return;
              },
            })}
            onKeyDown={async e => {
              if (e.key === "Enter") {
                e.preventDefault();
                await onSubmit();
              }
            }}
          />
        </InputRow>
      </>
    );

    return (
      <Modal onRequestClose={onCancel}>
        <CommonModal
          title="Add Link"
          dataTestId="add-link-form"
          onSubmit={onSubmit}
          onCancel={onCancel}
          size="small"
          primaryButtonText="Save"
          content={modalContent}
        />
      </Modal>
    );
  });

  return [showLinkModal, hideLinkModal] as const;
};
